import ScrollLandingPage from "components/landing/ScrollLandingPage";
import LandingPage from "components/landing/LandingPage";
import TeamPage from "components/landing/TeamPage";
import ContactPage from "components/landing/ContactPage";
import { jwtDecode } from "jwt-decode";
import { SnackbarProvider } from "notistack";
import { Suspense, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { RootState } from "types";
import logger from "utils/logger";

// Pages
import Dashboard from "./components/Dashboard";
import { BillingSettings } from "./components/billing";
import BillingGateListener from "./components/billing/BillingGateListener";
import ManageEnvironments from "./components/ManageEnvironments";
import BulkUploadResults from "./components/dashboards/BulkUploadResults";

// Auth & Routes
import NotFound from "components/NotFound/NotFound";
import PatientDetails from "components/PatientDetails/PatientDetails";
import LinkedInBotAuthPage from "components/communication/LinkedInBotAuthPage";
import LinkedInBotStart from "components/communication/LinkedInBotStart";
import LayoutContainer from "components/layout/LayoutContainer";
import LinkedInRedirectHandler from "components/social_login/LinkedInRedirectHandler";
import MicrosoftRedirectHandler from "components/social_login/MicrosoftRedirectHandler";
import PrivateRoute from "./components/PrivateRoute";
import { clearToken } from "./redux/authSlice";
import { AppDispatch } from "./redux/store";

// Admin
import SuperadminPanel from "components/admin/SuperadminPanel";

// Error handling & Network
import ErrorBoundary from "components/common/ErrorBoundary";
import PageLoader from "components/common/PageLoader";
import { useNetworkStatus } from "hooks/useNetworkStatus";

// Paths rendered outside LayoutContainer (full-page, own navbar/layout)
const STANDALONE_PATHS = ["/", "/landing", "/team", "/contact", "/management-team", "/our-team-old"];

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const token = useSelector((state: RootState) => state.auth.token);
  
  // Network status monitoring with notifications
  useNetworkStatus(true);
  
  logger.debug("AppContent path =", window.location.pathname);

  useEffect(() => {
    const path = window.location.pathname;

    // Public paths that don't require authentication
    const normalPath = path.replace(/\/+$/, "") || "/";
    const isPublicPath =
      STANDALONE_PATHS.includes(normalPath) || normalPath === "/login";
    const isAuthCallback =
      path.startsWith("/microsoft") || path.startsWith("/linkedin");

    logger.debug("AppContent path =", path, "hasToken =", !!token);

    if (!token) {
      logger.warn("No token in Redux, isAuthCallback =", isAuthCallback);
      if (!isAuthCallback && !isPublicPath) {
        navigate("/?signin=1");
      }
      return;
    }

    try {
      const decodedToken = jwtDecode<{ exp: number }>(token);
      if (decodedToken.exp * 1000 < Date.now()) {
        logger.warn("Token expired");
        dispatch(clearToken());
        if (!isAuthCallback && !isPublicPath) {
          navigate("/?signin=1");
        }
      }
    } catch (err) {
      logger.error("Token decode error:", err);
      dispatch(clearToken());
      if (!isAuthCallback && !isPublicPath) {
        navigate("/?signin=1");
      }
    }
  }, [token, navigate, dispatch]);

  // Standalone pages render outside LayoutContainer
  const isStandalone = STANDALONE_PATHS.includes(location.pathname);

  if (isStandalone) {
    return (
      <ErrorBoundary>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/landing" element={<ScrollLandingPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/contact" element={<ContactPage />} />
          {/* 301-equivalent redirects for old WordPress paths */}
          <Route path="/management-team" element={<Navigate to="/team" replace />} />
          <Route path="/our-team-old" element={<Navigate to="/team" replace />} />
        </Routes>
      </ErrorBoundary>
    );
  }

  return (
    <LayoutContainer>
      <ErrorBoundary>
        <Routes>
          {/* /login redirects to the landing page with sign-in form */}
          <Route path="/login" element={<Navigate to="/?signin=1" replace />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <ErrorBoundary>
                  <Dashboard />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />

          {/* <Route
            path="/patients/:id/details"
            element={
              <PrivateRoute>
                <PlatformUserDetails />
              </PrivateRoute>
            }
          /> */}
          <Route
            path="/patients/:id/details"
            element={
              <PrivateRoute>
                <ErrorBoundary>
                  <PatientDetails />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <PrivateRoute>
                <ErrorBoundary>
                  <BillingSettings />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />
          <Route
            path="/environments"
            element={
              <PrivateRoute>
                <ErrorBoundary>
                  <ManageEnvironments />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />
          <Route
            path="/bulk-upload/results/:jobId"
            element={
              <PrivateRoute>
                <ErrorBoundary>
                  <BulkUploadResults />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />
          <Route
            path="/superadmin"
            element={
              <PrivateRoute>
                <ErrorBoundary>
                  <SuperadminPanel />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />
        <Route path="/microsoft/*" element={<MicrosoftRedirectHandler />} />
        <Route path="/linkedin" element={<LinkedInRedirectHandler />} />


          {/* <Route path="*" element={<h2>404 Not Found</h2>} /> */}
          <Route path="/not-found" element={<NotFound />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ErrorBoundary>
    </LayoutContainer>
  );
}

function App() {
  return (
    <Suspense fallback={<PageLoader message="Loading application..." />}>
      <SnackbarProvider 
        maxSnack={3}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Router>
          <BillingGateListener />
          <AppContent />
        </Router>
      </SnackbarProvider>
    </Suspense>
  );
}

export default App;
